import {
    useInfiniteQuery,
    keepPreviousData as previousPlaceholder,
    InfiniteData,
    UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import isArray from 'lodash-es/isArray';

import { ApiDocumentsRecommendationResponse, ApiDocumentsResponse } from '../lib/api/Documents';

import { useApi } from '../contexts/ApiContext';

type UseDocumentsInfiniteQueryOptions = UseInfiniteQueryOptions<
    ApiDocumentsResponse,
    Error,
    InfiniteData<ApiDocumentsResponse, number>
    // ['documents', 'infinite_offset' | 'infinite_page', Record<string, unknown>],
    // number
>;

type UseDocumentsInfiniteOptions = Omit<
    UseDocumentsInfiniteQueryOptions,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
> & {
    withOffset?: boolean;
    keepPreviousData?: boolean;
};

export function createDocumentsInfiniteQuery(
    api,
    providedQuery,
    opts: UseDocumentsInfiniteOptions = null,
): Omit<UseDocumentsInfiniteQueryOptions, 'getNextPageParam'> {
    const { withOffset = false } = opts || {};
    const query = {
        count: 12,
        ...providedQuery,
    };
    const { offset = null, ...queryWithoutOffset } = query || {};
    return {
        initialPageParam: withOffset ? 0 : 1,
        queryKey: [
            'documents',
            withOffset ? 'infinite_offset' : 'infinite_page',
            withOffset ? queryWithoutOffset : query,
        ],
        queryFn: ({ pageParam = withOffset ? 0 : 1, queryKey: [, , queryParam], signal }) =>
            withOffset
                ? api.documents.get(
                      {
                          ...queryParam,
                          offset: pageParam,
                      },
                      null,
                      null,
                      { signal },
                  )
                : api.documents.get(queryParam, pageParam, null, {
                      signal,
                  }),
    };
}

export function prefetchDocumentsInfinite(queryClient, api, query, opts) {
    return queryClient.prefetchInfiniteQuery(createDocumentsInfiniteQuery(api, query, opts));
}

type UsePopularDocumentsInfiniteOptions = Omit<
    Parameters<
        typeof useInfiniteQuery<
            ApiDocumentsRecommendationResponse & { pagination: { total: number } },
            Error,
            InfiniteData<ApiDocumentsRecommendationResponse, number>,
            ['documents_popular_infinite', Record<string, unknown>],
            number
        >
    >[0],
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
> & {
    keepPreviousData?: boolean;
    withOffset?: boolean;
};

export function usePopularDocumentsInfinite(
    query = null,
    opts: UsePopularDocumentsInfiniteOptions = null,
) {
    const { withOffset = false, keepPreviousData = true, ...queryOpts } = opts || {};
    const api = useApi();
    const { data = null, ...queryResult } = useInfiniteQuery({
        initialPageParam: withOffset ? 0 : 1,
        queryKey: ['documents_popular_infinite', query],
        queryFn: ({ pageParam = 1, queryKey: [, queryParam], signal }) =>
            api.documents
                .popular(
                    {
                        ...queryParam,
                        page: pageParam,
                    },
                    20,
                    {
                        signal,
                    },
                )
                .then((it) =>
                    isArray(it)
                        ? {
                              data: it,
                              pagination: {
                                  total: 100,
                              },
                          }
                        : {
                              pagination: {
                                  total: 100,
                              },
                              ...it,
                          },
                ),
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...queryOpts,
    });

    const { pages = null, pageParams = null } = data || {};

    return {
        pages,
        pageParams,

        ...queryResult,
    };
}

export default function useDocumentsInfinite(
    query = null,
    opts: UseDocumentsInfiniteOptions = null,
) {
    const { withOffset = false, keepPreviousData = true, ...queryOpts } = opts || {};
    const api = useApi();
    const {
        data = null,
        isLoading,
        ...queryResult
    } = useInfiniteQuery({
        ...createDocumentsInfiniteQuery(api, query, opts),
        getNextPageParam: ({
            pagination: {
                page: currentPage = 1,
                next_offset: nextOffset = 0,
                last_page: lastPage = -1,
                total,
            },
        }) => {
            if ((withOffset && nextOffset === total) || (!withOffset && lastPage === currentPage)) {
                return undefined;
            }
            return withOffset ? nextOffset : currentPage + 1;
        },
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...queryOpts,
    });

    const { pages = null, pageParams = null } = data || {};

    const items =
        pages !== null
            ? (pages || []).reduce((acc, page) => [...acc, ...(page.data || [])], [])
            : null;

    const total = (pages || []).reduce<number | null>(
        (currentTotal, { pagination: { total: newTotal = 0 } }) =>
            currentTotal === null || newTotal > currentTotal ? newTotal : currentTotal,
        null,
    );

    return {
        pages,
        pageParams,
        items,
        total,
        endIsReached: !isLoading && total !== null && items !== null && total <= items.length,
        isLoading,
        ...queryResult,
    };
}
